import { Box, Button, Dialog, LinearProgress, Paper } from "@mui/material"
import { ErrorOutlineTwoTone as ErrorIcon } from "@mui/icons-material"
import { useCurrentUser } from "AppConfig/hooks"
import React, { useEffect } from "react"

const wrapperSx = {
  display: "flex",
  p: 3,
  mt: 5,
  ml: "auto",
  mr: "auto",
  maxWidth: 500,
} as const

const Wrapper: React.FC<{ inline: boolean; children?: React.ReactNode }> = ({ children, inline }) => {
  if (inline) {
    return (
      <Paper sx={wrapperSx} variant="outlined">
        {children}
      </Paper>
    )
  }
  return (
    <Dialog open>
      <Box sx={wrapperSx}>{children}</Box>
    </Dialog>
  )
}

const messages = {
  default:
    "We're sorry but something went wrong. We've been notified about this issue and we'll take a look at it shortly.",
  notAuthorized: "Sorry, you don't have permission to access this page",
  notFound: "Sorry, the page could not be found",
  conflict:
    "We detected a conflict while trying to save your changes. This may happen if you have the page open in several tabs at once. Please reload the page and check to make sure the data you entered is correct.",
  networkProblem:
    "Sorry, there was a network error connecting to the server. Please check your internet connection"
}

export type ErrorDialogType =
  | "notFound"
  | "notAuthorized"
  | "networkProblem"
  | "conflict"
  | "default"
interface Props {
  variant?: ErrorDialogType
  inline?: boolean
}
const EnvironmentErrorDialog: React.FC<Props> = ({ variant = "default", inline = false }) => {
  const user = useCurrentUser()
  const goToLogin = !user.id && variant === "notAuthorized"
  useEffect(() => {
    if (goToLogin) {
      const params = new URLSearchParams({
        return_to: window.location.pathname
      })
      window.location.href = `/login?${params.toString()}`
    }
  }, [goToLogin])

  if (goToLogin) {
    return (
      <Wrapper key="redir" inline={inline}>
        <div>
          <Box sx={(theme) => ({ ...theme.typography.h6, flexGrow: 1 })}>
            Loading login page &hellip;
          </Box>
          <LinearProgress variant="indeterminate" />
        </div>
      </Wrapper>
    )
  }

  const tryReload = variant === "networkProblem" || variant === "default"
  return (
    <Wrapper key="error" inline={inline}>
      <Box sx={{ mr: 2, flexGrow: 0 }}>
        <ErrorIcon fontSize="large" />
      </Box>
      <Box sx={(theme) => ({ ...theme.typography.h6, flexGrow: 1 })}>
        <Box sx={{ mb: 3 }}>{messages[variant]}</Box>
        <div className="text-right">
          {!tryReload && (
            <Button variant="contained" color="primary" component={"a"} href="/">
              Continue
            </Button>
          )}
          {tryReload && (
            <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          )}
        </div>
      </Box>
    </Wrapper>
  )
}
export default EnvironmentErrorDialog
